import { motion } from "framer-motion";
import { Phone, Mail, Clock, MapPin, MessageSquare, ShieldCheck } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white py-20 lg:py-32"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease }}
          className="font-mono-x text-xs uppercase tracking-[0.3em] text-red-600 font-semibold mb-4"
        >
          Get In Touch
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl mb-14"
        >
          One call. One text. One email. Done right.
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          <motion.a
            data-testid="contact-phone-link"
            href="tel:+13252345947"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease }}
            className="group bg-red-600 hover:bg-red-700 transition-colors duration-300 p-6 lg:p-8 flex flex-col justify-between min-h-[220px] active:scale-[0.99]"
          >
            <Phone size={32} className="text-white" />
            <div>
              <p className="font-mono-x text-xs uppercase tracking-[0.25em] text-red-100 mb-2">
                Call Us
              </p>
              <p className="font-display font-black text-white text-3xl md:text-[clamp(1.1rem,2.6vw,2.25rem)] tracking-tight whitespace-nowrap">
                325-234-5947
              </p>
            </div>
          </motion.a>

          <motion.a
            data-testid="contact-text-link"
            href="sms:+13252345947"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.05, ease }}
            className="group bg-white border-2 border-red-600 hover:bg-red-50 transition-colors duration-300 p-6 lg:p-8 flex flex-col justify-between min-h-[220px] active:scale-[0.99]"
          >
            <MessageSquare size={32} className="text-red-600" />
            <div>
              <p className="font-mono-x text-xs uppercase tracking-[0.25em] text-red-600 mb-2">
                Text Us
              </p>
              <p className="font-display font-black text-zinc-950 text-3xl md:text-[clamp(1.1rem,2.6vw,2.25rem)] tracking-tight whitespace-nowrap">
                325-234-5947
              </p>
            </div>
          </motion.a>

          <motion.a
            data-testid="contact-email-link"
            href="mailto:xtracleancarpet@gmail.com"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease }}
            className="group bg-zinc-950 hover:bg-zinc-900 transition-colors duration-300 p-6 lg:p-8 flex flex-col justify-between min-h-[220px] active:scale-[0.99]"
          >
            <Mail size={32} className="text-red-500" />
            <div>
              <p className="font-mono-x text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">
                Email Us
              </p>
              <p className="font-display font-bold text-white text-base md:text-[clamp(0.75rem,1.5vw,1.25rem)] tracking-tight whitespace-nowrap">
                xtracleancarpet@gmail.com
              </p>
            </div>
          </motion.a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15, ease }}
          className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          <div className="border-2 border-zinc-200 p-8 flex items-start gap-5">
            <Clock size={26} className="text-red-600 shrink-0 mt-1" />
            <div>
              <p className="font-display font-bold text-xl tracking-tight mb-1">
                Available 24/7 · Weekends Included
              </p>
              <p className="text-sm text-zinc-600 leading-relaxed">
                We answer calls any time, day or night, and we work weekends.
                Urgent move-in / move-out jobs welcome.
              </p>
            </div>
          </div>
          <div className="border-2 border-zinc-200 p-8 flex items-start gap-5">
            <MapPin size={26} className="text-red-600 shrink-0 mt-1" />
            <div>
              <p className="font-display font-bold text-xl tracking-tight mb-1">
                Serving San Angelo, TX
              </p>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Homes, rentals, offices &amp; storefronts across San Angelo and
                the surrounding area. Outside town? Just ask — the quote is free.
              </p>
            </div>
          </div>
          <div
            data-testid="contact-insured-card"
            className="border-2 border-zinc-200 p-8 flex items-start gap-5"
          >
            <ShieldCheck size={26} className="text-red-600 shrink-0 mt-1" />
            <div>
              <p className="font-display font-bold text-xl tracking-tight mb-1">
                Fully Insured
              </p>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Your home and property are protected on every job. Peace of
                mind comes standard with Xtra Clean.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
